import React, { useEffect, useMemo, useRef, useState } from 'react';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createChatSession, streamChatMessage } from '../utils/api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  corrections: string[];
  summaries: string[];
}

const LANGUAGES = ['EN', 'ZH', 'AR', 'FR', 'DE', 'ES', 'RU', 'JA', 'KO'];
const LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
const SCENARIOS = ['Daily Conversation', 'Job Interview', 'Travel', 'Business Meeting'];
const CORRECTION_PATTERN = /\[correction:\s*([^\]]+)\]/gi;
const SUMMARY_PATTERN = /\[summary:\s*([^\]]+)\]/gi;
const DECORATION_PATTERN = /\[(correction|summary):\s*[^\]]+\]/gi;

export function extractChatDecorations(text: string) {
  const corrections = Array.from(text.matchAll(CORRECTION_PATTERN), (match) => match[1].trim());
  const summaries = Array.from(text.matchAll(SUMMARY_PATTERN), (match) => match[1].trim());
  const cleanText = text.replace(DECORATION_PATTERN, '').trim();
  return { cleanText, corrections, summaries };
}

export default function ChatPage() {
  const [targetLanguage, setTargetLanguage] = useState('EN');
  const [level, setLevel] = useState('Intermediate');
  const [scenario, setScenario] = useState('Daily Conversation');
  const [focusWords, setFocusWords] = useState('nuance, context');
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const sessionLabel = useMemo(() => `${targetLanguage} · ${level} · ${scenario}`, [level, scenario, targetLanguage]);

  const handleCreateSession = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await createChatSession({
        target_language: targetLanguage.toLowerCase(),
        level: level.toLowerCase(),
        scenario,
        focus_words: focusWords
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      const nextSessionId = response.session_id;
      setSessionId(nextSessionId);
      const opening = response.opening_message || response.message || `Let's practice ${targetLanguage} for a ${scenario.toLowerCase()} scenario.`;
      setMessages([
        {
          id: `${nextSessionId}-intro`,
          role: 'assistant',
          text: opening,
          corrections: [],
          summaries: [],
        },
      ]);
    } catch (sessionError) {
      setError(sessionError instanceof Error ? sessionError.message : 'Unable to create chat session.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!draft.trim() || !sessionId || streaming) {
      return;
    }
    const userText = draft.trim();
    setDraft('');
    setStreaming(true);
    setError('');

    const assistantId = `assistant-${Date.now()}`;
    setMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, role: 'user', text: userText, corrections: [], summaries: [] },
      { id: assistantId, role: 'assistant', text: '', corrections: [], summaries: [] },
    ]);

    try {
      await streamChatMessage(
        sessionId,
        userText,
        (delta) => {
          setMessages((current) =>
            current.map((message) =>
              message.id === assistantId ? { ...message, text: `${message.text}${delta}` } : message
            )
          );
        },
        (fullMessage) => {
          const parsed = extractChatDecorations(fullMessage);
          setMessages((current) =>
            current.map((message) =>
              message.id === assistantId
                ? { ...message, text: parsed.cleanText, corrections: parsed.corrections, summaries: parsed.summaries }
                : message
            )
          );
        }
      );
    } catch (messageError) {
      setError(messageError instanceof Error ? messageError.message : 'Unable to send message.');
      setMessages((current) => current.filter((message) => message.id !== assistantId));
    } finally {
      setStreaming(false);
    }
  };

  const handleReset = () => {
    setSessionId('');
    setMessages([]);
    setDraft('');
    setError('');
  };

  if (!sessionId) {
    return (
      <Card sx={{ borderRadius: 3, maxWidth: 760 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>Start a conversation</Typography>
            <Typography color="text.secondary">Configure a practice session and DictZoom will stream AI replies with guidance, corrections, and summaries.</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField select label="Target language" value={targetLanguage} onChange={(event) => setTargetLanguage(event.target.value)} fullWidth>
                {LANGUAGES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
              <TextField select label="Level" value={level} onChange={(event) => setLevel(event.target.value)} fullWidth>
                {LEVELS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField select label="Scenario" value={scenario} onChange={(event) => setScenario(event.target.value)} fullWidth>
                {SCENARIOS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
              <TextField label="Focus words" value={focusWords} onChange={(event) => setFocusWords(event.target.value)} fullWidth helperText="Comma-separated words or themes" />
            </Stack>
            <Button variant="contained" onClick={handleCreateSession} disabled={loading} sx={{ alignSelf: 'flex-start', bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
              {loading ? 'Starting...' : 'Start Conversation'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>AI Chat Practice</Typography>
              <Typography color="text.secondary">{sessionLabel}</Typography>
            </Box>
            <Button startIcon={<AddCommentRoundedIcon />} variant="outlined" onClick={handleReset}>New Session</Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}

      <Card sx={{ borderRadius: 3, minHeight: 520, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: '#f8fafc' }}>
            <Stack spacing={2.5}>
              {messages.map((message) => (
                <Stack key={message.id} alignItems={message.role === 'user' ? 'flex-end' : 'flex-start'}>
                  <Box
                    sx={{
                      maxWidth: '80%',
                      px: 2,
                      py: 1.5,
                      borderRadius: 3,
                      bgcolor: message.role === 'user' ? '#111827' : '#fff',
                      color: message.role === 'user' ? '#fff' : '#111827',
                      boxShadow: message.role === 'assistant' ? '0 12px 32px rgba(15,23,42,0.06)' : 'none',
                    }}
                  >
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text || (streaming && message.role === 'assistant' ? '...' : '')}</Typography>
                  </Box>
                  <Stack spacing={1} sx={{ mt: 1, width: '80%' }}>
                    {message.corrections.map((item) => (
                      <Alert key={item} severity="success">Correction: {item}</Alert>
                    ))}
                    {message.summaries.map((item) => (
                      <Alert key={item} severity="info">Summary: {item}</Alert>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box sx={{ p: 2.5, borderTop: '1px solid #e5e7eb', bgcolor: '#fff' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Type your message..."
                fullWidth
                multiline
                maxRows={4}
              />
              <Button variant="contained" endIcon={<SendRoundedIcon />} onClick={handleSend} disabled={streaming} sx={{ px: 3, py: 1.4, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
                Send
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
              <Chip label={`Language: ${targetLanguage}`} size="small" />
              <Chip label={`Scenario: ${scenario}`} size="small" />
              <Chip label={`Level: ${level}`} size="small" />
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}
